import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { FeatureGate, FeatureLockedBadge, hasFeatureAccess } from './FeatureGate';
import { formsAPI } from '../utils/api';
import { 
  BarChart3, TrendingUp, Eye, Send, Globe, Monitor, 
  Smartphone, Clock, Users, ArrowUpRight, ArrowDownRight,
  RefreshCw, Calendar, Percent, MousePointerClick
} from 'lucide-react';

/**
 * AnalyticsTab - Per-form analytics with tiered access
 * 
 * Free: Basic counts only
 * Growth: Conversion rate, daily trends
 * Pro: Geographic, device, hourly, drop-off analysis
 */
export const AnalyticsTab = ({ formId }) => {
  const { usage } = useAuth();
  const currentPlan = usage?.plan || 'free';
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('7d'); // 7d, 30d, 90d

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const response = await formsAPI.getAnalytics(formId, dateRange);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  }, [formId, dateRange]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  const conversionRate = analytics?.total_views > 0 
    ? ((analytics?.total_submissions / analytics?.total_views) * 100).toFixed(1) 
    : 0;

  return (
    <div className="space-y-6">
      {/* Header with date range selector */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Form Analytics</h3>
          <p className="text-sm text-slate-500">Track your form performance</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={dateRange === '7d' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setDateRange('7d')}
          >
            7 Days
          </Button>
          <Button
            variant={dateRange === '30d' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setDateRange('30d')}
          >
            30 Days
          </Button>
          <Button
            variant={dateRange === '90d' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setDateRange('90d')}
          >
            90 Days
          </Button>
        </div>
      </div>

      {/* Basic Stats - Available to all */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Views"
          value={analytics?.total_views || 0}
          icon={Eye}
          color="blue"
        />
        <StatCard
          title="Total Submissions"
          value={analytics?.total_submissions || 0}
          icon={Send}
          color="green"
        />
        
        {/* Conversion Rate - Growth+ */}
        {hasFeatureAccess(currentPlan, 'growth') ? (
          <StatCard
            title="Conversion Rate"
            value={`${conversionRate}%`}
            icon={Percent}
            color="purple"
            trend={analytics?.conversion_trend}
          />
        ) : (
          <LockedStatCard title="Conversion Rate" requiredPlan="growth" />
        )}

        {/* Avg. Completion Time - Growth+ */}
        {hasFeatureAccess(currentPlan, 'growth') ? (
          <StatCard
            title="Avg. Time"
            value={analytics?.avg_completion_time || '0s'}
            icon={Clock}
            color="orange"
          />
        ) : (
          <LockedStatCard title="Avg. Time" requiredPlan="growth" />
        )}
      </div>

      {/* Daily Trends Chart - Growth+ */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Daily Trends
            {!hasFeatureAccess(currentPlan, 'growth') && (
              <FeatureLockedBadge requiredPlan="growth" />
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <FeatureGate
            requiredPlan="growth"
            currentPlan={currentPlan}
            featureName="Daily Trends"
            variant="overlay"
          >
            <DailyTrendsChart data={analytics?.daily_trends || []} />
          </FeatureGate>
        </CardContent>
      </Card>

      {/* Pro Analytics Section */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Geographic Data - Pro Only */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Geographic Distribution
              {!hasFeatureAccess(currentPlan, 'pro') && (
                <FeatureLockedBadge requiredPlan="pro" />
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <FeatureGate
              requiredPlan="pro"
              currentPlan={currentPlan}
              featureName="Geographic Analytics"
              variant="overlay"
            >
              <GeographicChart data={analytics?.geographic || []} />
            </FeatureGate>
          </CardContent>
        </Card>

        {/* Device Breakdown - Pro Only */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Monitor className="w-4 h-4" />
              Device Breakdown
              {!hasFeatureAccess(currentPlan, 'pro') && (
                <FeatureLockedBadge requiredPlan="pro" />
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <FeatureGate
              requiredPlan="pro"
              currentPlan={currentPlan}
              featureName="Device Analytics"
              variant="overlay"
            >
              <DeviceBreakdown data={analytics?.devices || {}} />
            </FeatureGate>
          </CardContent>
        </Card>
      </div>

      {/* Hourly Patterns - Pro Only */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Hourly Activity Pattern
            {!hasFeatureAccess(currentPlan, 'pro') && (
              <FeatureLockedBadge requiredPlan="pro" />
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <FeatureGate
            requiredPlan="pro"
            currentPlan={currentPlan}
            featureName="Hourly Analytics"
            variant="overlay"
          >
            <HourlyPatternChart data={analytics?.hourly_pattern || []} />
          </FeatureGate>
        </CardContent>
      </Card>

      {/* Drop-off Analysis - Pro Only (for multi-step forms) */}
      {analytics?.has_steps && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <MousePointerClick className="w-4 h-4" />
              Step Drop-off Analysis
              {!hasFeatureAccess(currentPlan, 'pro') && (
                <FeatureLockedBadge requiredPlan="pro" />
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <FeatureGate
              requiredPlan="pro"
              currentPlan={currentPlan}
              featureName="Drop-off Analysis"
              variant="overlay"
            >
              <DropOffAnalysis data={analytics?.step_dropoff || []} />
            </FeatureGate>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, color, trend }) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
  };

  return (
    <Card>
      <CardContent className="pt-4">
        <div className="flex items-center justify-between">
          <div className={`w-10 h-10 rounded-lg ${colors[color]} flex items-center justify-center`}>
            <Icon className="w-5 h-5" />
          </div>
          {trend !== undefined && (
            <span className={`text-xs font-medium flex items-center ${
              trend >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
              {Math.abs(trend)}%
            </span>
          )}
        </div>
        <div className="mt-3">
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          <p className="text-sm text-slate-500">{title}</p>
        </div>
      </CardContent>
    </Card>
  );
};

// Locked Stat Card
const LockedStatCard = ({ title, requiredPlan }) => {
  return (
    <Card className="relative overflow-hidden">
      <CardContent className="pt-4 opacity-50">
        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
          <BarChart3 className="w-5 h-5 text-slate-400" />
        </div>
        <div className="mt-3">
          <p className="text-2xl font-bold text-slate-400">--</p>
          <p className="text-sm text-slate-400">{title}</p>
        </div>
      </CardContent>
      <div className="absolute inset-0 flex items-center justify-center bg-white/60">
        <FeatureLockedBadge requiredPlan={requiredPlan} />
      </div>
    </Card>
  );
};

// Daily Trends Chart (Simple bar visualization)
const DailyTrendsChart = ({ data }) => {
  if (!data || data.length === 0) {
    return <p className="text-sm text-slate-500 text-center py-8">No data available yet</p>;
  }

  const maxViews = Math.max(...data.map(d => d.views), 1);
  const maxSubmissions = Math.max(...data.map(d => d.submissions), 1);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 text-xs">
        <span className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-blue-500" /> Views
        </span>
        <span className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-green-500" /> Submissions
        </span>
      </div>
      <div className="flex items-end gap-1 h-32">
        {data.map((day, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full flex gap-0.5 items-end h-24">
              <div 
                className="flex-1 bg-blue-500 rounded-t transition-all"
                style={{ height: `${(day.views / maxViews) * 100}%`, minHeight: day.views > 0 ? '4px' : '0' }}
                title={`${day.views} views`}
              />
              <div 
                className="flex-1 bg-green-500 rounded-t transition-all"
                style={{ height: `${(day.submissions / maxSubmissions) * 100}%`, minHeight: day.submissions > 0 ? '4px' : '0' }}
                title={`${day.submissions} submissions`}
              />
            </div>
            <span className="text-[10px] text-slate-400">
              {new Date(day.date).toLocaleDateString('en', { weekday: 'short' })}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Geographic Chart
const GeographicChart = ({ data }) => {
  if (!data || data.length === 0) {
    return <p className="text-sm text-slate-500 text-center py-8">No geographic data yet</p>;
  }

  const total = data.reduce((sum, d) => sum + d.count, 0);

  return (
    <div className="space-y-3">
      {data.slice(0, 5).map((item, i) => (
        <div key={i} className="flex items-center gap-3">
          <span className="text-lg">{item.flag || '🌍'}</span>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium">{item.country}</span>
              <span className="text-xs text-slate-500">{item.count} ({((item.count / total) * 100).toFixed(0)}%)</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-indigo-500 rounded-full"
                style={{ width: `${(item.count / total) * 100}%` }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Device Breakdown
const DeviceBreakdown = ({ data }) => {
  const devices = [
    { key: 'desktop', label: 'Desktop', icon: Monitor, color: 'bg-blue-500' },
    { key: 'mobile', label: 'Mobile', icon: Smartphone, color: 'bg-green-500' },
    { key: 'tablet', label: 'Tablet', icon: Monitor, color: 'bg-purple-500' },
  ];

  const total = Object.values(data).reduce((sum, v) => sum + (v || 0), 0) || 1;

  return (
    <div className="space-y-4">
      <div className="flex h-4 rounded-full overflow-hidden bg-slate-100">
        {devices.map(device => {
          const value = data[device.key] || 0;
          const percent = (value / total) * 100;
          return percent > 0 ? (
            <div
              key={device.key}
              className={`${device.color} transition-all`}
              style={{ width: `${percent}%` }}
              title={`${device.label}: ${value}`}
            />
          ) : null;
        })}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {devices.map(device => {
          const Icon = device.icon;
          const value = data[device.key] || 0;
          const percent = ((value / total) * 100).toFixed(0);
          return (
            <div key={device.key} className="text-center">
              <Icon className="w-5 h-5 mx-auto text-slate-400 mb-1" />
              <p className="text-lg font-semibold">{percent}%</p>
              <p className="text-xs text-slate-500">{device.label}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Hourly Pattern Chart
const HourlyPatternChart = ({ data }) => {
  if (!data || data.length === 0) {
    return <p className="text-sm text-slate-500 text-center py-8">No hourly data yet</p>;
  }

  const max = Math.max(...data.map(d => d.count), 1);

  return (
    <div className="flex items-end gap-0.5 h-24">
      {data.map((hour, i) => (
        <div key={i} className="flex-1 flex flex-col items-center">
          <div 
            className="w-full bg-indigo-500 rounded-t transition-all hover:bg-indigo-600"
            style={{ height: `${(hour.count / max) * 100}%`, minHeight: hour.count > 0 ? '2px' : '0' }}
            title={`${hour.hour}:00 - ${hour.count} submissions`}
          />
          {i % 4 === 0 && (
            <span className="text-[9px] text-slate-400 mt-1">{hour.hour}h</span>
          )}
        </div>
      ))}
    </div>
  );
};

// Drop-off Analysis for Multi-step Forms
const DropOffAnalysis = ({ data }) => {
  if (!data || data.length === 0) {
    return <p className="text-sm text-slate-500 text-center py-8">No step data available</p>;
  }

  return (
    <div className="space-y-3">
      {data.map((step, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-semibold text-sm">
            {i + 1}
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium">{step.label || `Step ${i + 1}`}</span>
              <span className="text-xs text-slate-500">
                {step.completed} completed
                {step.dropoff_rate > 0 && (
                  <span className="text-red-500 ml-2">-{step.dropoff_rate}% drop</span>
                )}
              </span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-indigo-500 rounded-full transition-all"
                style={{ width: `${step.completion_rate}%` }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AnalyticsTab;
